import React, { useCallback } from 'react';
import { PromiseState } from 'react-refetch';

import { extent } from 'd3-array';
import { scaleTime, scaleLinear } from 'd3-scale';
import { timeFriday } from 'd3-time';

import { useColorScale } from 'components/data-containers/PerformanceTrend/hooks/useColorScale';
import useTrendData from './utils/useTrendData';

import LineChart from 'components/Overview/LineChart/LineChart';
import { ITrend, IItem } from './types';

interface ITrendChart {
  trendData: PromiseState<ITrend[]>;
  isPending: boolean;
}

/**
 * TrendChart creates a Overview specific composition for the
 * LineChart component. The configuration and creation of scales specific for
 * this component is done here.
 *
 * @param {object} props React props
 * @param {PromiseState} props.trendData trend data PromiseState
 * @param {boolean} props.isPending Is the data fetch currently pending?
 */
const TrendChart: React.FC<ITrendChart> = (props) => {
  const { trendData, isPending } = props;

  /**
   * Prepare the trend data for use
   */
  const trends = useTrendData(trendData);

  /**
   * Is this the initial load?
   */
  const isFirstLoad = isPending && trends.length === 0;

  /**
   * Returns the extent of values for the key specified from all trends' data
   * arrays.
   *
   * @param {array} trends Array of trends
   * @param {string} key Key to use for accessing values
   * @returns {array} Array with two values, min/max.
   */
  function getTrendExtent(trends: ITrend[], key: string): any {
    return extent(
      trends.flatMap((el: ITrend) => el.data.map((d: IItem) => d[key]))
    );
  }

  /**
   * Get the dates extent
   */
  const dateExtent = getTrendExtent(trends, 'periodKey');

  /**
   * Get the values extent
   */
  const valueExtent = getTrendExtent(trends, 'value');

  // Set the start of the value extent to 0 if it is above 0
  if (Number(valueExtent[0]) >= 0) {
    valueExtent[0] = 0;
  }

  /**
   * Create the horizontal scale
   */
  const x = useCallback(scaleTime().domain(dateExtent).nice(timeFriday), [
    dateExtent,
    timeFriday,
  ]);

  /**
   * Create the vertical scale
   */
  const y = useCallback(scaleLinear().domain(valueExtent).nice(), [
    dateExtent,
    timeFriday,
  ]);

  /**
   * Create a color scale
   */
  const color = useColorScale(trends);

  return (
    <LineChart
      caption="Last 30 days"
      trends={trends}
      xScale={x}
      yScale={y}
      colorScale={color}
      isPending={isPending}
      isFirstLoad={isFirstLoad}
    />
  );
};

export default TrendChart;
